/*
 * @providesModule SignInStyle
 */

import {Platform, StyleSheet} from 'react-native';
import Sizes from 'Sizes';
import SkillzStyle from 'SkillzStyle';

const headerTextFont = Sizes.isTablet() ? 24 : 20;

const defaultStyles = StyleSheet.create({
  activeTab: {
    color: 'rgba(0, 0, 0, 0.9)',
    borderBottomColor: 'rgba(0, 0, 0, 0.9)',
    fontFamily: 'OpenSans-Bold',
    fontWeight: Platform.OS !== 'ios' ? 'bold' : '600',
  },
  closeBtn: {
    alignSelf: 'center',
    position: 'absolute',
    bottom: Sizes.isTablet() ? 20 : 16,
    right: Sizes.isTablet() ? 44 : 29,
  },
  closeIcons: {
    height: Sizes.isTablet() ? 32 : 24,
    width: Sizes.isTablet() ? 32 : 24,
  },
  container: {
    alignItems: 'center',
    backgroundColor: '#ffffff',
    height: '100%',
    width: '100%',
    alignSelf: 'center',
  },
  containerScrollview: {
    backgroundColor: '#ffffff',
    height: '100%',
    width: '100%',
    paddingTop: 1,
  },
  headerContainer: {
    width: '100%',
  },
  headerText: {
    position: 'absolute',
    bottom: Sizes.isTablet() ? 20 : 16,
    alignSelf: 'flex-end',
    fontFamily: 'OpenSans-Bold',
    fontSize: headerTextFont,
    color: 'rgba(0, 0, 0, 0.9)',
  },
  helpModalContentWrapper: {
    marginHorizontal: 16,
    width: 'auto',
  },
  helpModalContainer: {
    paddingHorizontal: 32,
    alignItems: 'center',
    width: '100%',
  },
  helpModalBody: {
    color: 'rgba(0, 0, 0, 0.9)',
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '400',
    fontStyle: 'normal',
    fontFamily: 'OpenSans-Regular',
    marginTop: 16,
    marginBottom: 32,
    width: '100%',
  },
  helpModalTitle: {
    fontFamily: 'OpenSans-Bold',
    fontStyle: 'normal',
    fontWeight: Platform.OS !== 'ios' ? 'bold' : '700',
    fontSize: 20,
    lineHeight: 28,
    color: '#000',
    textAlign: 'left',
    width: '100%',
    marginTop: 32,
  },
  inActiveTab: {
    color: 'rgba(0, 0, 0, 0.5)',
    borderBottomColor: 'rgba(0, 0, 0, 0.5)',
    fontFamily: 'OpenSans-Regular',
  },
  leftIcon: {
    height: 23,
    width: 23,
    top: 3,
  },
  leftIconContainer: {
    height: 23,
  },
  rightIcon: {
    height: 23,
    width: 23,
  },
  tab: {
    flex: 2,
    borderBottomWidth: 2,
  },
  tabContainer: {
    width: '100%',
    flexDirection: 'row',
    marginTop: 28,
    height: 32,
  },
  tabText: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
});

const darkStyles = StyleSheet.create({
  container: {
    ...defaultStyles.container,
    backgroundColor: '#1B1B1B',
  },
  containerScrollview: {
    ...defaultStyles.containerScrollview,
    backgroundColor: '#1B1B1B',
  },
  activeTab: {
    ...defaultStyles.activeTab,
    color: '#fff',
    borderBottomColor: '#fff',
  },
  inActiveTab: {
    ...defaultStyles.inActiveTab,
    color: 'rgba(255, 255, 255, 0.6)',
    borderBottomColor: 'rgba(255, 255, 255, 0.6)',
  },
});

export const getStyles = (colorVariant?: 'dark') => {
  switch (colorVariant) {
    case 'dark':
      return {
        ...defaultStyles,
        ...darkStyles,
      };
    default:
      return defaultStyles;
  }
};

export const createStyles = (gameTheme: any, isDarkTheme: boolean) => {
  const {
    textColorPrimary,
    textColorTertiary,
    textColorSecondary,
    fontRegular,
    fontSizeH4,
    fontSizeH5,
  } = gameTheme ?? {};

  return StyleSheet.create({
    container: {
      flex: 1,
      position: 'relative',
    },
    backIcon: {
      width: 24,
      height: 24,
    },
    infoIcon: {
      width: 24,
      height: 24,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 20,
      paddingTop: 10,
      paddingBottom: 24,
    },
    scrollViewContent: {
      flexDirection: 'column',
      justifyContent: 'space-between',
      width: '100%',
      alignSelf: 'center',
      flexGrow: 1,
      paddingVertical: 38,
      paddingHorizontal: 32,
    },
    loginWithText: {
      fontSize: 16,
      ...SkillzStyle.fullStyles.fontBold,
      fontStyle: 'normal',
      fontFamily: 'Open Sans',
      textDecorationColor: textColorPrimary.color,
      textDecorationLine: 'underline',
      color: textColorPrimary.color,
      textAlign: 'center',
      marginTop: 20,
    },
    tosHeaderContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 20,
    },
    tosTitle: {
      fontSize: 18,
      ...SkillzStyle.fullStyles.fontBold,
      color: isDarkTheme ? textColorPrimary?.color : textColorSecondary?.color,
    },
    tosCloseButton: {
      fontSize: 16,
      ...SkillzStyle.fullStyles.fontNormal,
    },
    tabWithDynamicBorder: {
      borderBottomWidth: 1,
    },
    orContinueWithContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 16,
    },
    orContinueWithText: {
      ...fontRegular,
      ...fontSizeH4,
      ...textColorTertiary,
      marginHorizontal: 10,
      textAlign: 'center',
    },
    line: {
      width: 90,
      height: 3,
    },
    normalText: {
      ...fontRegular,
      ...fontSizeH5,
      ...textColorTertiary,
      textAlign: 'center',
    },
    linkText: {
      ...fontRegular,
      ...fontSizeH5,
      ...textColorTertiary,
      textDecorationLine: 'underline',
    },
    socialButtonsContainer: {
      flexDirection: 'row',
      justifyContent: 'center',
      marginTop: 24,
      marginBottom: 32,
      gap: 16,
    },
    buttonWrapper: {
      position: 'relative',
    },
    socialButton: {
      // flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.90)' : 'rgba(0, 0, 0, 0.80)',
      opacity: 0.86,
      borderRadius: 16,
      paddingVertical: 12,
      paddingHorizontal: 6,
      shadowOffset: {
        width: 1,
        height: 1,
      },
      shadowColor: 'rgba(0, 0, 0, 0.25)',
      shadowOpacity: 1,
      shadowRadius: 6,
      elevation: 2,
    },
    socialIcon: {
      width: 20,
      height: 20,
      marginRight: 6,
      // color: textColorPrimary?.color,
    },
    socialButtonText: {
      ...SkillzStyle.fullStyles.fontBold,
      fontSize: 14,
      // ...textColorPrimary,
      color: isDarkTheme ? 'black' : 'white',
    },
    tosView: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 999,
    },
  });
};

export default defaultStyles;
